import React, { useEffect, useState, forwardRef } from 'react';
import { Box } from '@material-ui/core';
import { SSearchInputV2, SSelectDropdown } from '@setvi/shared/components';
import { DropdownContent } from './content';

export interface SSearchSelectDropdownProps<T> {
  list: T[];
  loading?: boolean;
  placeholder?: string;
  search: string;
  dropdownWidth?: string;
  dropdownHeight?: string;
  containerStyle?: React.CSSProperties;
  setSearch: (search: string) => void;
  isSelected: (item: T) => boolean;
  onSelect: (item: T) => void;
  onSearchSubmit: (search: string) => void;
  renderRow: (item: T) => React.ReactNode;
}

function SSearchSelectDropdownComponent<T>(
  props: SSearchSelectDropdownProps<T>,
  ref: React.Ref<HTMLInputElement>
) {
  const {
    list,
    loading,
    search,
    placeholder = 'Search...',
    dropdownWidth = '600px',
    dropdownHeight = 'unset',
    containerStyle = {},
    isSelected,
    setSearch,
    onSelect,
    onSearchSubmit,
    renderRow
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSelect = (item: T) => {
    onSelect(item);
    setDropdownOpen(false);
    (ref as React.MutableRefObject<HTMLInputElement>)?.current?.focus();
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        (ref as React.MutableRefObject<HTMLInputElement>)?.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [ref]);

  useEffect(() => {
    if (!dropdownOpen && search.length > 0 && list.length > 0) {
      setDropdownOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, list]);

  return (
    <SSelectDropdown
      dropdownOpen={dropdownOpen}
      onClose={() => {
        setDropdownOpen(false);
      }}
      placement="bottom-start"
      popperOptions={{
        strategy: 'fixed',
        modifiers: [
          {
            name: 'flip',
            enabled: false
          }
        ]
      }}
      anchor={
        <SSearchInputV2
          ref={ref}
          onClick={e => {
            if (list.length > 0) {
              setDropdownOpen(true);
              e.stopPropagation();
            }
          }}
          search={search}
          placeholder={placeholder}
          onChange={onSearchSubmit}
          setSearch={setSearch}
          width="100%"
        />
      }
      content={
        <Box
          style={{
            width: dropdownWidth,
            height: dropdownHeight,
            overflow: 'auto',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            ...containerStyle
          }}>
          <DropdownContent
            list={list}
            loading={loading}
            isSelected={isSelected}
            onSelect={handleSelect}
            renderRow={renderRow}
            search={search}
          />
        </Box>
      }
    />
  );
}

export const SSearchSelectDropdown = forwardRef(
  SSearchSelectDropdownComponent
) as <T>(
  props: SSearchSelectDropdownProps<T> & { ref?: React.Ref<HTMLInputElement> }
) => JSX.Element;

export default SSearchSelectDropdown;
